// Company-admin AI Assistant settings (main-app, Model A). Connect a per-tenant
// provider key + manage the knowledge base. Gated to ai-config:write.
//
// SECURITY: the key input is masked with an opt-in show/hide so the admin can
// verify their paste BEFORE saving; the key is never logged and is sent only in
// the save request. After saving it's write-only — the server returns only the
// last 4 digits, never the key itself.
import { formatDate } from '@/lib/orgFormat';
import React, { useEffect, useState } from 'react';
import {
  Bot, ShieldCheck, FileText, Inbox, Trash2, Loader2, Eye, EyeOff, ShieldAlert, Languages,
} from 'lucide-react';
import { useRbacRoles } from '@/rbac/useRbac';
import { hasPermission } from '@/rbac/rbac.generated';
import {
  getAiConfig, saveAiConfig, clearAiConfig, CLAUDE_MODELS, OPENAI_MODELS, PROVIDERS, AiConfig,
  listDocuments, uploadDocument, deleteDocument, HRDoc,
  saveDanaConfig, DanaConfig, DanaStyle, DanaLanguage, DanaToggles,
  saveLocalizationConfig, LocalizationConfig,
} from '@/lib/aiConfigApi';
import { getApiErrorMessage } from '@/lib/apiError';

const CUSTOM = '__custom__';

export const AiAssistantPage: React.FC = () => {
  const roles = useRbacRoles();
  const canManage = hasPermission(roles, 'ai-config:write');

  const [config, setConfig] = useState<AiConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [provider, setProvider] = useState<'anthropic' | 'openai'>('anthropic');
  const [model, setModel] = useState('claude-sonnet-4-6');
  const [customModel, setCustomModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  // Dana chat-history retention (days); '' = use the platform default.
  const [retentionDays, setRetentionDays] = useState<string>('');
  const [note, setNote] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const [docs, setDocs] = useState<HRDoc[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [dana, setDana] = useState<DanaConfig | null>(null);
  const [danaSaving, setDanaSaving] = useState(false);

  // Regional voice — tenants TUNE within the platform grant (dana_localization);
  // the card only renders when KaziPeople has granted the feature.
  const [loc, setLoc] = useState<LocalizationConfig | null>(null);
  const [locCountry, setLocCountry] = useState<string | null>(null);
  const [locSaving, setLocSaving] = useState(false);

  const isCurated = (m?: string | null) => !!m && CLAUDE_MODELS.some((c) => c.value === m);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const c = await getAiConfig();
      setConfig(c);
      setRetentionDays(c.chatRetentionDays != null ? String(c.chatRetentionDays) : '');
      if (c.dana) setDana(c.dana);
      if (c.localization?.granted) {
        setLoc({
          primary_language: 'sw', secondary_languages: ['en'], code_switching: true,
          regional_lingo: true, lingo_strength: 'moderate', formality: 'professional_friendly',
          ...(c.localization.config ?? {}),
        });
        setLocCountry(c.localization.country ?? null);
      }
      if (c.model && isCurated(c.model)) setModel(c.model);
        if (c.provider === 'openai' || c.provider === 'anthropic') setProvider(c.provider);
      else if (c.model) { setModel(CUSTOM); setCustomModel(c.model); }
    } catch {
      setNote({ kind: 'err', text: 'Could not load AI settings.' });
    } finally {
      setLoading(false);
    }
  };

  const loadDocs = async () => {
    setDocsLoading(true);
    try { setDocs(await listDocuments()); }
    catch { /* keep empty */ }
    finally { setDocsLoading(false); }
  };

  useEffect(() => { if (canManage) { loadConfig(); loadDocs(); } }, [canManage]);

  const effectiveModel = model === CUSTOM ? customModel.trim() : model;

  const save = async () => {
    if (!effectiveModel) { setNote({ kind: 'err', text: 'Please choose or enter a model.' }); return; }
    setSaving(true); setNote(null);
    try {
      const c = await saveAiConfig({
        provider,
        model: effectiveModel,
        apiKey: apiKey.trim() || undefined, // omit to keep the existing key
        chatRetentionDays: retentionDays.trim() === '' ? null : Number(retentionDays),
      });
      setConfig(c);
      setRetentionDays(c.chatRetentionDays != null ? String(c.chatRetentionDays) : '');
      setApiKey('');       // never keep the plaintext key around after saving
      setShowKey(false);
      setNote({ kind: 'ok', text: 'AI settings saved.' });
    } catch (e: any) {
      setNote({ kind: 'err', text: getApiErrorMessage(e, 'Could not save AI settings.') });
    } finally {
      setSaving(false);
    }
  };

  const removeKey = async () => {
    setSaving(true); setNote(null);
    try {
      const c = await clearAiConfig();
      setConfig(c);
      setNote({ kind: 'ok', text: 'API key removed.' });
    } catch (e) {
      setNote({ kind: 'err', text: getApiErrorMessage(e, 'Could not remove the key.') });
    } finally {
      setSaving(false);
    }
  };

  const saveDana = async () => {
    if (!dana) return;
    setDanaSaving(true); setNote(null);
    try {
      const d = await saveDanaConfig({
        style: dana.style, language: dana.language,
        replyTo: dana.replyTo || null, toggles: dana.toggles,
        workingHours: dana.workingHours,
      });
      setDana(d);
      setNote({ kind: 'ok', text: 'Dana settings saved.' });
    } catch (e: any) {
      setNote({ kind: 'err', text: getApiErrorMessage(e, 'Could not save Dana settings.') });
    } finally {
      setDanaSaving(false);
    }
  };

  const onUpload = async (file: File) => {
    setUploading(true); setNote(null);
    try {
      const d = await uploadDocument(file);
      setNote({ kind: 'ok', text: `Indexed “${d.name}” (${d.chunk_count} chunks).` });
      loadDocs();
    } catch (e: any) {
      setNote({ kind: 'err', text: e?.response?.data?.detail || 'Could not index that document.' });
    } finally {
      setUploading(false);
    }
  };

  const onDeleteDoc = async (id: string) => {
    try { await deleteDocument(id); loadDocs(); }
    catch (e) { setNote({ kind: 'err', text: getApiErrorMessage(e, 'Could not remove the document.') }); }
  };

  if (!canManage) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <h1 className="text-h3 text-charcoal">Admins only</h1>
        <p className="text-muted-foreground mt-1">You don't have permission to manage AI settings.</p>
      </div>
    );
  }

  const card = 'rounded-2xl border border-border bg-card p-6';
  const label = 'block text-sm font-medium text-foreground mb-1.5';
  const input = 'w-full h-11 px-3 rounded-lg border border-border bg-card text-foreground text-sm focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100';
  const primaryBtn = 'h-10 px-4 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50';

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-h2 text-charcoal flex items-center gap-2"><Bot className="w-6 h-6" /> AI Assistant</h1>
        <p className="text-muted-foreground mt-1">
          Connect your own AI provider key so your team can ask the assistant about your organization and
          leave. Usage is billed to your account. The key is stored encrypted and never shown again after
          saving.
        </p>
      </div>

      {note && (
        <div className={`rounded-lg px-3 py-2 text-sm border ${
          note.kind === 'ok' ? 'bg-success-soft border-success text-success-strong' : 'bg-danger-soft border-danger text-danger-strong'
        }`}>
          {note.text}
        </div>
      )}

      {/* Provider key */}
      <div className={card}>
        {loading ? (
          <div className="py-8 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <>
            <div className={`rounded-lg px-3 py-2 text-sm mb-5 border ${
              config?.configured || config?.managedKey ? 'bg-success-soft border-success text-success-strong' : 'bg-info-soft border-info text-info-strong'
            }`}>
              {config?.configured ? (
                <span>
                  API key configured <span className="font-mono">•••• {config.keyLast4}</span>
                  {config.configuredAt ? ` · set ${formatDate(config.configuredAt)}` : ''}
                </span>
              ) : config?.managedKey ? (
                <span>
                  Using the <strong>platform default</strong>
                  {config.platformDefault?.provider ? (
                    <>
                      {' — '}
                      {config.platformDefault.provider === 'openai' ? 'OpenAI' : 'Anthropic'}
                      {config.platformDefault.model ? ` · ${config.platformDefault.model}` : ''}
                    </>
                  ) : ''}
                  . Dana works without your own key — add one below to use your own account instead.
                </span>
              ) : (
                'No API key set yet — the assistant is disabled until you add one.'
              )}
            </div>

            <div className="space-y-4">
              <div>
                <span className={label}>Provider</span>
                <select
                  className={input}
                  value={provider}
                  onChange={(e) => {
                    const p = e.target.value as 'anthropic' | 'openai';
                    setProvider(p);
                    setModel(p === 'openai' ? 'gpt-4o-mini' : 'claude-sonnet-4-6');
                  }}
                >
                  {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  The key below must match the provider. GPT-4o mini gives the snappiest Dana Voice.
                </p>
              </div>

              <div>
                <span className={label}>Model</span>
                <select className={input} value={model} onChange={(e) => setModel(e.target.value)}>
                  {(provider === 'openai' ? OPENAI_MODELS : CLAUDE_MODELS).map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  <option value={CUSTOM}>Custom model id…</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">Models evolve — pick a current one, or enter a custom model id.</p>
              </div>

              {model === CUSTOM && (
                <div>
                  <span className={label}>Custom model id</span>
                  <input className={input} value={customModel} onChange={(e) => setCustomModel(e.target.value)} placeholder="e.g. claude-…" />
                </div>
              )}

              <div>
                <span className={label}>API key</span>
                <div className="relative">
                  <input
                    className={`${input} pr-11 font-mono`}
                    type={showKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={config?.configured ? '•••••••••• (unchanged)' : provider === 'openai' ? 'sk-…' : 'sk-ant-…'}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey((s) => !s)}
                    aria-label={showKey ? 'Hide key' : 'Show key'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {config?.configured
                    ? 'Leave blank to keep the current key. Enter a new key to replace it.'
                    : 'Paste your Anthropic API key (sk-ant-…). Use the eye to check it before saving — it’s never shown again after.'}
                </p>
              </div>

              <div>
                <span className={label}>Chat history retention (days)</span>
                <input
                  className={`${input} w-32`}
                  type="number"
                  min={1}
                  max={30}
                  value={retentionDays}
                  onChange={(e) => setRetentionDays(e.target.value)}
                  placeholder="default"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  How many days Dana keeps each person’s chat history before it’s deleted. HR chat is
                  sensitive, so keep it short. Leave blank for the platform default. (1–30 days.)
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button className={primaryBtn} disabled={saving} onClick={save}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save
                </button>
                {config?.configured && (
                  <button
                    className="h-10 px-4 rounded-lg border border-danger text-danger hover:bg-red-50 inline-flex items-center gap-2 disabled:opacity-50"
                    disabled={saving}
                    onClick={removeKey}
                  >
                    Remove key
                  </button>
                )}
              </div>
            </div>

            <div className="mt-5 pt-4 border-t border-border text-sm text-muted-foreground flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
              <p>
                <span className="font-medium text-foreground">Safety:</span> the assistant is restricted to
                your company's HR and organization questions, and can only access data within each user's
                reporting hierarchy. Get a key from{' '}
                <a className="text-orange-600 hover:underline" href="https://console.anthropic.com/" target="_blank" rel="noreferrer">
                  console.anthropic.com
                </a>.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Dana — identity & voice */}
      {dana && (
        <div className={card}>
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2"><Bot className="w-5 h-5" /> Dana — identity &amp; voice</h2>
          <p className="text-muted-foreground text-sm mt-1 mb-4">
            Dana is your AI HR Assistant — a permanent part of KaziPeople. You can't rename her, but you can
            tune how she communicates with your team.
          </p>

          {/* Brand-locked identity (read-only) */}
          <div className="rounded-lg border border-border bg-muted/40 p-3 mb-5 flex items-center gap-3">
            <img src="/brand/kazipeople-logo-120.png" alt="Dana" className="w-10 h-10 rounded-xl shrink-0" />
            <div className="text-sm min-w-0">
              <div className="font-semibold text-foreground truncate">{dana.identity.name} · {dana.identity.title}</div>
              <div className="text-muted-foreground truncate">{dana.identity.email} · {dana.identity.employeeId}</div>
            </div>
            <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0">Brand-locked</span>
          </div>

          <div className="space-y-4">
            <div>
              <span className={label}>Communication style</span>
              <select className={input} value={dana.style} onChange={(e) => setDana({ ...dana, style: e.target.value as DanaStyle })}>
                <option value="professional">Professional — courteous &amp; polished</option>
                <option value="friendly">Friendly — warm, a little emoji</option>
                <option value="corporate">Corporate — formal &amp; precise</option>
                <option value="government">Government — official &amp; impersonal</option>
              </select>
            </div>

            <div>
              <span className={label}>Language</span>
              <select className={input} value={dana.language} onChange={(e) => setDana({ ...dana, language: e.target.value as DanaLanguage })}>
                <option value="auto">Auto-detect (English / Swahili)</option>
                <option value="english">English</option>
                <option value="swahili">Swahili</option>
              </select>
            </div>

            <div>
              <span className={label}>Reply-to address <span className="font-normal text-muted-foreground">(optional)</span></span>
              <input
                className={input}
                type="email"
                value={dana.replyTo ?? ''}
                placeholder="hr@yourcompany.com"
                onChange={(e) => setDana({ ...dana, replyTo: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">Dana's emails come from <span className="font-mono">dana@kazipeople.com</span>; replies go to this address.</p>
            </div>

            <div className="pt-1 divide-y divide-border">
              {([
                ['proactiveNotifications', 'Proactive notifications', 'Master switch for all of Dana’s proactive messages'],
                ['weeklyDigest', 'Weekly digest (Mondays)', 'Team/manager/HR summary email'],
                ['birthdayMessages', 'Birthday messages', ''],
                ['anniversaryMessages', 'Work anniversary messages', ''],
                ['onboardingNudges', 'Onboarding nudges', 'Reminds new joiners to finish their profile'],
              ] as [keyof DanaToggles, string, string][]).map(([k, lbl, hint]) => {
                // proactiveNotifications is the master switch — the rest depend on it.
                const disabled = k !== 'proactiveNotifications' && !dana.toggles.proactiveNotifications;
                return (
                  <label key={k} className={`flex items-center justify-between py-2.5 cursor-pointer ${disabled ? 'opacity-50' : ''}`}>
                    <span className="text-sm text-foreground">
                      {lbl}
                      {hint ? <span className="block text-xs text-muted-foreground">{hint}</span> : null}
                    </span>
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-orange-500"
                      disabled={disabled}
                      checked={dana.toggles[k]}
                      onChange={(e) => setDana({ ...dana, toggles: { ...dana.toggles, [k]: e.target.checked } })}
                    />
                  </label>
                );
              })}
            </div>

            {/* Working hours — Dana avoids sending proactive messages outside these. */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Working hours</label>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="time"
                  className={input + ' w-auto'}
                  value={dana.workingHours.start}
                  onChange={(e) => setDana({ ...dana, workingHours: { ...dana.workingHours, start: e.target.value } })}
                />
                <span className="text-muted-foreground text-sm">to</span>
                <input
                  type="time"
                  className={input + ' w-auto'}
                  value={dana.workingHours.end}
                  onChange={(e) => setDana({ ...dana, workingHours: { ...dana.workingHours, end: e.target.value } })}
                />
                <span className="text-xs text-muted-foreground">({dana.workingHours.tz})</span>
              </div>
            </div>

            <button className={primaryBtn} disabled={danaSaving} onClick={saveDana}>
              {danaSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save Dana settings
            </button>
          </div>
        </div>
      )}

      {/* Regional voice & language — only when KaziPeople granted dana_localization */}
      {loc && (
        <div className={card}>
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2"><Languages className="w-5 h-5" /> Regional voice &amp; language</h2>
          <p className="text-muted-foreground text-sm mt-1 mb-4">
            How Dana speaks for {locCountry === 'TZ' ? 'Tanzania' : locCountry || 'your country'} — language mix,
            conversational style and regional persona. This changes how Dana sounds, never what she's allowed to do.
            Serious topics (payroll, compliance, disciplinary) always stay precise and professional.
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <span className={label}>Region</span>
              <select className={input} value={loc.region ?? ''} onChange={(e) => setLoc({ ...loc, region: e.target.value })}>
                <option value="">National</option>
                {locCountry === 'TZ' && <option value="dar_es_salaam">Dar es Salaam</option>}
                {locCountry === 'KE' && <option value="nairobi">Nairobi</option>}
              </select>
            </div>
            <div>
              <span className={label}>Persona</span>
              <select
                className={input}
                value={loc.persona_pack ?? ''}
                onChange={(e) => setLoc({ ...loc, persona_pack: e.target.value, persona_version: e.target.value ? 'v1' : undefined })}
              >
                <option value="">Auto (from country/region)</option>
                {locCountry === 'TZ' && <option value="tz-dar">Tanzania — Dar es Salaam (v1)</option>}
                {locCountry === 'TZ' && <option value="tz-standard">Tanzania — Standard (v1)</option>}
                {locCountry === 'KE' && <option value="ke-standard">Kenya — Standard (v1)</option>}
              </select>
            </div>
            <div>
              <span className={label}>Primary language</span>
              <select className={input} value={loc.primary_language ?? 'sw'} onChange={(e) => setLoc({ ...loc, primary_language: e.target.value as 'sw' | 'en' })}>
                <option value="sw">Kiswahili</option>
                <option value="en">English</option>
              </select>
            </div>
            <div>
              <span className={label}>Local flavor</span>
              <select className={input} value={loc.lingo_strength ?? 'moderate'} onChange={(e) => setLoc({ ...loc, lingo_strength: e.target.value as LocalizationConfig['lingo_strength'] })}>
                <option value="low">Low — mostly standard speech</option>
                <option value="moderate">Moderate — natural local conversation</option>
                <option value="strong">Strong — relaxed local style</option>
              </select>
            </div>
            <div>
              <span className={label}>Formality</span>
              <select className={input} value={loc.formality ?? 'professional_friendly'} onChange={(e) => setLoc({ ...loc, formality: e.target.value as LocalizationConfig['formality'] })}>
                <option value="relaxed">Relaxed</option>
                <option value="professional_friendly">Professional, friendly</option>
                <option value="professional">Professional</option>
                <option value="formal">Formal</option>
              </select>
            </div>
          </div>

          <div className="mt-4 divide-y divide-border">
            <label className="flex items-center justify-between py-2.5 cursor-pointer">
              <span className="text-sm text-foreground">
                Natural code switching
                <span className="block text-xs text-muted-foreground">Dana mirrors the user's English/Kiswahili mix — never forces it</span>
              </span>
              <input type="checkbox" className="h-4 w-4 accent-orange-500" checked={loc.code_switching ?? true}
                onChange={(e) => setLoc({ ...loc, code_switching: e.target.checked })} />
            </label>
            <label className="flex items-center justify-between py-2.5 cursor-pointer">
              <span className="text-sm text-foreground">
                Regional conversational style
                <span className="block text-xs text-muted-foreground">Local workplace rhythm and terms; off = standard speech only</span>
              </span>
              <input type="checkbox" className="h-4 w-4 accent-orange-500" checked={loc.regional_lingo ?? true}
                onChange={(e) => setLoc({ ...loc, regional_lingo: e.target.checked })} />
            </label>
          </div>

          <button
            className={primaryBtn + ' mt-3'}
            disabled={locSaving}
            onClick={async () => {
              setLocSaving(true);
              try {
                const r = await saveLocalizationConfig(loc);
                setLoc((prev) => ({ ...prev, ...r.config }));
                setNote({ kind: 'ok', text: 'Regional voice settings saved.' });
              } catch (e) {
                setNote({ kind: 'err', text: getApiErrorMessage(e, 'Could not save regional voice settings.') });
              } finally {
                setLocSaving(false);
              }
            }}
          >
            {locSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save regional voice
          </button>
        </div>
      )}

      {/* Knowledge base */}
      <div className={card}>
        <h2 className="text-lg font-bold text-foreground flex items-center gap-2"><FileText className="w-5 h-5" /> Knowledge base</h2>
        <p className="text-muted-foreground text-sm mt-1 mb-4">
          Upload your company's HR documents — employee handbook, leave policy, contracts. The assistant
          searches these to answer policy questions, and cites them. Documents are private to your company.
        </p>

        <label className="block cursor-pointer rounded-xl border-2 border-dashed border-orange-300/60 hover:border-orange-400 transition-colors p-8 text-center">
          <input
            type="file"
            accept=".pdf,.txt,.md"
            className="hidden"
            disabled={uploading}
            onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
          />
          <Inbox className="w-8 h-8 mx-auto text-orange-500" />
          <div className="mt-2 font-medium text-foreground">{uploading ? 'Indexing…' : 'Click or drag a file to upload'}</div>
          <div className="text-sm text-muted-foreground mt-0.5">PDF, TXT or Markdown.</div>
        </label>

        <div className="mt-4">
          {docsLoading ? (
            <div className="py-6 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : docs.length === 0 ? (
            <div className="py-6 text-center text-muted-foreground text-sm">No documents yet.</div>
          ) : (
            <ul className="divide-y divide-border">
              {docs.map((d) => (
                <li key={d.id} className="py-3 flex items-center gap-3">
                  <FileText className="w-5 h-5 text-orange-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground truncate">{d.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{d.chunk_count} chunks</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${d.status === 'ready' ? 'bg-success-soft text-success-strong' : 'bg-danger-soft text-danger-strong'}`}>{d.status}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => onDeleteDoc(d.id)}
                    aria-label="Remove document"
                    className="text-muted-foreground hover:text-red-600 p-1.5"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiAssistantPage;
